import { CalendarCheck2, GraduationCap, LineChart, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import type { SchoolSummary, Portal } from '../types'
import { useGuardians } from '../guardians'
import { portalsFor } from '../portals'
import { cn } from '@/utils/cn'

const highlights = [
  { icon: GraduationCap, text: 'Admissions, enrollment and promotions in a few clicks' },
  { icon: CalendarCheck2, text: 'Academic years, classes, sections and curriculum' },
  { icon: LineChart, text: 'Live dashboards for leadership and the school office' },
]

export function BrandPanel({ school, portal }: { school: SchoolSummary | null; portal: Portal }) {
  const { t } = useTranslation('auth')
  const guardians = useGuardians()
  return (
    <aside className="bg-brand-gradient relative hidden overflow-hidden p-10 text-white lg:flex lg:flex-col lg:justify-between xl:p-14">
      {/* Decorative grid + orbs */}
      <svg aria-hidden="true" className="absolute inset-0 h-full w-full opacity-[0.12]">
        <defs>
          <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M32 0H0v32" fill="none" stroke="white" strokeWidth="0.6" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>
      <div
        aria-hidden="true"
        className="absolute -right-24 -bottom-24 size-96 rounded-full bg-white/10 blur-2xl"
      />
      <div
        aria-hidden="true"
        className="absolute top-1/3 -left-16 size-64 rounded-full bg-accent-400/30 blur-3xl"
      />

      <div className="relative">
        <div className="flex items-center gap-3">
          <span className="grid size-12 place-items-center overflow-hidden rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
            {school?.logoUrl ? (
              <img src={school.logoUrl} alt="" className="size-full object-contain" />
            ) : (
              <GraduationCap className="size-6" />
            )}
          </span>
          <div>
            <p className="text-sm font-semibold">{school?.name ?? 'SchoolEduca'}</p>
            <p className="text-xs text-white/70">{t('School management platform')}</p>
          </div>
        </div>

        <h1 className="mt-14 max-w-md text-4xl leading-[1.1] font-semibold tracking-tight text-white xl:text-[2.75rem]">
          {t('Everything your school runs on, in one calm place.')}
        </h1>
        <ul className="mt-8 space-y-3.5">
          {highlights.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-3 text-sm text-white/85">
              <span className="grid size-8 place-items-center rounded-lg bg-white/10 ring-1 ring-white/15">
                <Icon className="size-4" aria-hidden="true" />
              </span>
              {t(text)}
            </li>
          ))}
        </ul>
      </div>

      <nav aria-label={t('Portals')} className="relative mt-10 grid gap-2">
        <p className="mb-1 text-xs font-medium tracking-wide text-white/60 uppercase">
          {t('Choose your workspace')}
        </p>
        {portalsFor(guardians).map(({ key, label, description, icon: Icon }) => {
          const current = key === portal
          return (
            <Link
              key={key}
              to={`/login?portal=${key}`}
              aria-current={current ? 'true' : undefined}
              className={cn(
                'flex items-center gap-3 rounded-xl px-4 py-3 ring-1 backdrop-blur transition-all',
                current
                  ? 'bg-white text-brand-700 shadow-lg ring-white'
                  : 'bg-white/[0.07] text-white ring-white/15 hover:bg-white/[0.14]',
              )}
            >
              <Icon className="size-5 shrink-0" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-sm font-semibold">{t(label)}</span>
                <span className={cn('block text-xs', current ? 'text-muted' : 'text-white/65')}>
                  {t(description)}
                </span>
              </span>
              {current ? <ShieldCheck className="ml-auto size-4 text-brand-500" aria-hidden="true" /> : null}
            </Link>
          )
        })}
      </nav>
    </aside>
  )
}
